import { globalStores, TGlobalStores } from '@/editor/stores';
import BaseEntity from '@/editor/entities/BaseEntity';
import { IPoint2d } from '@/editor/math/IPoint2d';
import { clientPosToWorldPos, convertWorldPositionToNdc } from '@/editor/utils';
import RectangleHelper from '@/editor/entities/2d/rectangleHelper';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { rayIntersect } from '../operateEntityController/rayIntersect';
import BoxSelectedEntity from '@/editor/entities/selectedEntity/boxSelectedEntity';
import { EEntityType, ISelected } from '@/editor/types';
import {
  ENTITY_RENDER_ORDER,
  hoverOutlineColor,
  MULTI_SELECT_TEMPORARY_ID,
  selectedOutlineColor
} from '@/editor/constant/entity';
import { IBoxOutlineParams } from '@/editor/entities/type';
import { createMultiModelData, getMultiModelBoxes } from '@/editor/data/multiModel/utils';
import { IModelInfo, IMultiModelData } from '@/editor/data/multiModel/multiModel';
import editorEvent, { EditorEventName } from '@/editor/event/editorEvent';
import { CommonController2d } from '../CommonController';
import SeparateWallEntity from '@/editor/entities/separateWallEntity';
import SeparateSelectedEntity from '@/editor/entities/selectedEntity/separateSelectedEntity';
import { modelManager } from '@/editor/manager/entityManager';
import { EEntityStatus2 } from '@/editor/types/entity';
import { ESepcialKey } from '@/editor/stores/keyboardStore';
import { FLAGS } from '@/flags';

const windowSelectionColor = 0x4c9dff;
const crossingSelectionColor = 0x1cdc87;
const allowMultiSelectedEntityType = [EEntityType.Furniture, EEntityType.Assembly, EEntityType.SeparateWall];

/**
 * 起止点小于这个距离（屏幕像素）就算点选而不是框选。
 *
 * 必须在屏幕空间上比：同一段世界距离在不同缩放下对应的像素数差几个数量级，
 * 100% 缩放下 10mm 不足半个像素，点击时手抖一下就会被判成框选。
 */
export const BOX_SELECT_MIN_PIXEL_DISTANCE = 4;

export default class MultiSelect extends CommonController2d {
  checkedEntity: BaseEntity | null | undefined = null; // 弃用

  private _startPoint: IPoint2d | null = null;
  private _endPoint: IPoint2d = { x: 0, y: 0 };
  /** 起点的屏幕坐标，与 _startPoint 同生共死，只用于区分点选与框选 */
  private _startClientPoint: IPoint2d | null = null;
  /** 光标当前的屏幕坐标，兼作框选终点的判据与跟随光标的选择模式提示的定位 */
  private _endClientPoint: IPoint2d = { x: 0, y: 0 };
  private camera = globalStores.editorStateStore.sceneInfo.scene2d.camera;

  private _multiModelData: IMultiModelData | null = null;

  private _selectionRectHelper = new RectangleHelper();
  private _hoverBoxesMap: Map<string, BoxSelectedEntity[]> = new Map();
  private _selectedBoxHelpers: BoxSelectedEntity[] = [];
  /** 隔墙不画包围盒，hover 时叠一层墙体形状的高亮，与 SelectedHoveredManager 的单选 hover 一致 */
  private _hoverSepWallsMap: Map<string, SeparateSelectedEntity> = new Map();
  /** 隔墙的选中态直接打在墙体上，这里记下来好复位 */
  private _selectedSepWallEntities: SeparateWallEntity[] = [];

  private _firstDraw = true;
  private _firstMove = true;
  private _drawing = false;
  private _isComplete = false; // 是否已经完成选择
  private _hoverRaf: number | null = null;
  private _onlyFurniture = false; // 是否只能框选家具，每次框选起手时重置，框选途中由 Tab 切换

  private _hintEmitted = false;

  private _disposeSet: Set<() => void> = new Set();

  private get _isSingleSelect() {
    if (!this._startClientPoint) return false;
    return Point2dUtils.distanceTo(this._startClientPoint, this._endClientPoint) < BOX_SELECT_MIN_PIXEL_DISTANCE;
  }

  /** 正在拉框：已经拖出足够距离，不是点选 */
  private get _isBoxSelecting() {
    return this._drawing && !!this._startPoint && !this._isSingleSelect;
  }

  constructor(_gs: TGlobalStores) {
    super(_gs);

    this._disposeSet
      .add(
        editorEvent.on(EditorEventName.KEYBOARD_PRESS_UPDATE, () => {
          if (globalStores.keyboardStore.isMultiSelectModifierPressed() || this._drawing) return;
          this.handleComplete();
        })
      )
      .add(
        editorEvent.on(EditorEventName.ENTITY_CLEAR, () => {
          this._multiModelData = null;
          this.updateHoverBoxHelpers([]);
          this.updateSelectedHelpers();
          this.notifyMultiSelectData();
        })
      )
      // 交接进来的那一次左键按下，走正常的 mouseDown 流程
      .add(editorEvent.on(EditorEventName.MULTI_SELECT_TAKEOVER_MOUSE_DOWN, (event) => this.mouseDown(event)))
      // 拉框途中按 Tab 在「仅家具」与「家具+隔墙」之间切换
      .add(
        editorEvent.on(EditorEventName.KEYBOARD_KEY_PRESS, (key) => {
          if (key !== ESepcialKey.Tab || !this._isBoxSelecting) return;
          this._onlyFurniture = !this._onlyFurniture;
          this.refreshBoxSelection();
        })
      );

    const currentHover = this._gs.editorStateStore.hoverEntity;

    if (currentHover && ![...allowMultiSelectedEntityType, EEntityType.MultiModel].includes(currentHover.type)) {
      this._gs.editorStateStore.clearHoverEntity();
    }

    // 没按修饰键说明是左键拖拽起的框选，「替换」语义：旧选中直接丢掉，不接管
    if (!this._gs.keyboardStore.isMultiSelectModifierPressed()) {
      this._gs.editorStateStore.clearSelected();
      return;
    }

    const currentSelected = this._gs.editorStateStore.selected;

    if (!this._isValidMultiSelectData(currentSelected)) {
      return;
    }

    switch (currentSelected.type) {
      case EEntityType.Furniture:
      case EEntityType.Assembly:
      case EEntityType.SeparateWall:
        this._multiModelData = createMultiModelData(
          globalStores.multiModelOperation.transformModelInfos([currentSelected])
        );
        break;
      case EEntityType.MultiModel: {
        this._multiModelData = globalStores.getDataByIdType('', EEntityType.MultiModel) ?? null;
        break;
      }
      default:
        break;
    }

    if (!this._multiModelData) return;

    // 多选已转换为内部状态，clearSelected 会顺带熄掉 SelectedHoveredManager 打的高亮，之后由这里自己维护
    this._gs.editorStateStore.clearSelected();
    this.updateSelectedHelpers();
    this.notifyMultiSelectData();
  }

  override mouseDown(e: MouseEvent) {
    const {
      renderer,
      scene2d: { camera }
    } = this._gs.editorStateStore.sceneInfo;
    const worldPosition = clientPosToWorldPos(e, camera, renderer);

    this._startPoint = worldPosition;
    this._endPoint = worldPosition;
    this._startClientPoint = { x: e.clientX, y: e.clientY };
    this._endClientPoint = this._startClientPoint;
    this._drawing = true;
    // 每次框选起手都回到默认的「家具+隔墙」，上一次按 Tab 切出来的模式不带过来
    this._onlyFurniture = false;

    if (this._firstDraw) {
      this._firstDraw = false;

      // 开始框选或点击时，检查当前selected是否为非可多选模型，不是则要清空
      if (!this._isValidMultiSelectData(this._gs.editorStateStore.selected)) {
        globalStores.editorStateStore.clearSelected();
      }
    }
  }

  override mouseMove(e: MouseEvent) {
    if (this._firstMove) {
      this._gs.editorStateStore.clearHoverEntity();
    }

    this._endClientPoint = { x: e.clientX, y: e.clientY };

    const {
      renderer,
      scene2d: { camera }
    } = this._gs.editorStateStore.sceneInfo;
    const worldPosition = clientPosToWorldPos(e, camera, renderer);
    this._endPoint = worldPosition;

    if (!this._startPoint) {
      this.refreshPointHover();
      this.updateBoxSelectHint();
      return;
    }

    this.refreshBoxSelection();
  }

  /**
   * 按光标当前位置刷新 hover。
   * 已在多选集中的构件同样要出 hover——点它是把它移出多选集，没有 hover 就看不出点哪个。
   */
  private refreshPointHover() {
    const ndc = convertWorldPositionToNdc(this._endPoint, this.camera);
    const [hovered] = rayIntersect.rayEntities(ndc);
    this.updateHoverBoxHelpers(hovered && this._isValidEntity(hovered) ? [hovered] : []);
  }

  /** 按当前选择模式重画选框与命中预览，Tab 切换模式后也走这里 */
  private refreshBoxSelection() {
    if (!this._startPoint) return;

    if (this._isSingleSelect) {
      this._selectionRectHelper.clear();
    } else {
      this.updateSelectionRectHelper(this._startPoint, this._endPoint);
    }

    const intersectedEntities = getRectSelecteds(globalStores, this._startPoint, this._endPoint, {
      onlyFurniture: this._onlyFurniture
    });

    this.updateHoverBoxHelpers(intersectedEntities);
    this.updateBoxSelectHint();
    // 只有真正拉框时才用预览集合；还没拖开时是待定的点选，面板保持已选内容
    this.notifyMultiSelectData(this._isBoxSelecting ? this.getBoxSelectingModelInfos(intersectedEntities) : undefined);
  }

  /**
   * 把当前多选内容同步给参数面板。多选期间 selected 是空的，面板只能靠这条通知刷新。
   *
   * @param multiSelectingModelInfos 拉框途中的预览集合；不传则用已落定的多选数据
   */
  private notifyMultiSelectData(multiSelectingModelInfos?: IModelInfo[]) {
    editorEvent.emit(
      EditorEventName.MULTI_SELECT_DATA_CHANGE,
      multiSelectingModelInfos ?? this._multiModelData?.modelInfos ?? []
    );
  }

  /** 拉框途中「此刻松手会选中什么」，口径与 mouseUp 的框选分支保持一致 */
  private getBoxSelectingModelInfos(intersectedEntities: ISelected[]) {
    return globalStores.multiModelOperation.transformModelInfos(
      intersectedEntities,
      FLAGS.FEATURES.ENABLE_RECT_SElECT_TOGGLE ? this._multiModelData?.modelInfos : []
    );
  }

  /** 同步跟随光标的选择模式提示，非拉框状态下只发一次 null */
  private updateBoxSelectHint() {
    if (!this._isBoxSelecting) {
      if (!this._hintEmitted) return;
      this._hintEmitted = false;
      editorEvent.emit(EditorEventName.BOX_SELECT_HINT_CHANGE, null);
      return;
    }

    this._hintEmitted = true;
    editorEvent.emit(EditorEventName.BOX_SELECT_HINT_CHANGE, {
      x: this._endClientPoint.x,
      y: this._endClientPoint.y,
      onlyFurniture: this._onlyFurniture
    });
  }

  override mouseUp(e: MouseEvent, isDragged: boolean) {
    if (isDragged) return;

    if (this._isComplete) return;

    this._drawing = false;
    if (!this._startPoint) return;

    const addedList = [];

    if (this._isSingleSelect) {
      const ndc = convertWorldPositionToNdc(this._endPoint, this.camera);
      const intersectedEntities = rayIntersect.rayEntities(ndc);
      if (intersectedEntities.length && this._isValidEntity(intersectedEntities[0])) {
        addedList.push(intersectedEntities[0]);
      }
    } else {
      const includedEntities = getRectSelecteds(globalStores, this._startPoint, this._endPoint, {
        onlyFurniture: this._onlyFurniture
      });
      addedList.push(...includedEntities);
    }

    const modelInfos = globalStores.multiModelOperation.transformModelInfos(
      addedList,
      FLAGS.FEATURES.ENABLE_RECT_SElECT_TOGGLE || this._isSingleSelect ? this._multiModelData?.modelInfos : []
    );
    // 含隔墙时选中框不再是子盒的并集（隔墙不出子盒），容器盒统一由 createMultiModelData 算
    this._multiModelData = modelInfos.length ? createMultiModelData(modelInfos) : null;

    this._startPoint = null;
    this._startClientPoint = null;

    // 松手后光标还停在原处，按当前位置重算 hover；直接清空会让刚被移出多选集的构件既无选中态也无 hover
    this.refreshPointHover();
    this.updateSelectedHelpers();
    this._selectionRectHelper.clear();
    this.updateBoxSelectHint();
    this.notifyMultiSelectData();

    this.handleComplete();
  }

  private handleComplete = () => {
    if (globalStores.keyboardStore.isMultiSelectModifierPressed() || this._drawing) return;

    this._isComplete = true;

    if (this._multiModelData) {
      // 隔墙高亮交给 SelectedHoveredManager 接管，这里先松手，否则下面 dispose() 会把它刚点亮的墙体熄掉
      this._selectedSepWallEntities = [];

      // if (this._multiModelData.modelInfos.length === 1) {
      //   this._gs.editorStateStore.setSelected({
      //     entityId: this._multiModelData.modelInfos[0].id,
      //     type: this._multiModelData.modelInfos[0].type
      //   });
      // } else {
      //   this._gs.editorStateStore.setSelected(
      //     {
      //       entityId: MULTI_SELECT_TEMPORARY_ID,
      //       type: EEntityType.MultiModel
      //     },
      //     { multiModelData: this._multiModelData }
      //   );
      // }

      // 多选的交互先传多选数据，如果只选了单个在operateEntityController里面再处理
      this._gs.editorStateStore.setSelected(
        {
          entityId: MULTI_SELECT_TEMPORARY_ID,
          type: EEntityType.MultiModel
        },
        { multiModelData: this._multiModelData }
      );
    }

    this.dispose();
    this.exit();
  };

  private updateSelectionRectHelper(start: IPoint2d, end: IPoint2d) {
    const width = Math.abs(end.x - start.x);
    const height = Math.abs(end.y - start.y);
    const center = { x: (start.x + end.x) / 2, y: (start.y + end.y) / 2 };
    const isWindowSelection = start.x - end.x < 0;
    const color = isWindowSelection ? windowSelectionColor : crossingSelectionColor;

    this._selectionRectHelper.update({
      width,
      height,
      center,
      backgroundColor: color,
      lineType: isWindowSelection ? 'solid' : 'dashed',
      lineColor: color,
      zoom: globalStores.scene2dZoom
    });
  }

  private updateHoverBoxHelpers(items: ISelected[]) {
    if (this._hoverRaf) {
      cancelAnimationFrame(this._hoverRaf);
      this._hoverRaf = null;
    }

    const newIdSet = new Set(items.map(({ entityId }) => entityId));
    this._hoverRaf = requestAnimationFrame(() => {
      // 清除旧的
      for (const [id, box] of this._hoverBoxesMap.entries()) {
        if (!newIdSet.has(id)) {
          box.forEach((b) => b.dispose());
          this._hoverBoxesMap.delete(id);
        }
      }
      for (const [id, sepWallHelper] of this._hoverSepWallsMap.entries()) {
        if (!newIdSet.has(id)) {
          sepWallHelper.dispose();
          this._hoverSepWallsMap.delete(id);
        }
      }

      for (const { entityId, type } of items) {
        // 隔墙的轴对齐盒对斜墙/弧墙明显大于墙体，hover 也不画盒，改成墙体形状的高亮
        if (type === EEntityType.SeparateWall) {
          if (this._hoverSepWallsMap.has(entityId)) continue;
          const sepWallHelper = createSepWallHoverHelper(entityId);
          if (sepWallHelper) this._hoverSepWallsMap.set(entityId, sepWallHelper);
          continue;
        }

        if (!this._hoverBoxesMap.has(entityId)) {
          const boxes: IBoxOutlineParams[] = [];
          switch (type) {
            case EEntityType.Furniture: {
              boxes.push(...getFurnitureBoxes(entityId));
              break;
            }
            case EEntityType.Assembly: {
              boxes.push(...getAssemblyBoxes(entityId));
              break;
            }
          }
          if (boxes.length > 0) {
            this._hoverBoxesMap.set(
              entityId,
              boxes.map((data) => {
                const helper = new BoxSelectedEntity();
                helper.update(data);
                return helper;
              })
            );
          }
        }
      }
    });
  }

  private updateSelectedBoxHelpers(boxes: IBoxOutlineParams[]) {
    this._selectedBoxHelpers.forEach((helper) => {
      helper.dispose();
    });

    this._selectedBoxHelpers = boxes.map((data) => {
      const helper = new BoxSelectedEntity();
      helper.update(data);
      return helper;
    });
  }
  /** 刷新当前多选的选中表现：盒状构件画包围盒，隔墙把墙体本身置成选中态 */
  private updateSelectedHelpers() {
    const modelInfos = this._multiModelData?.modelInfos ?? [];
    this.updateSelectedSepWallEntities(modelInfos);
    this.updateSelectedBoxHelpers(this._multiModelData ? this.getBoxData(this._multiModelData) : []);
  }

  private updateSelectedSepWallEntities(modelInfos: IModelInfo[]) {
    const sepWallIds = new Set(
      modelInfos.filter((modelInfo) => modelInfo.type === EEntityType.SeparateWall).map(({ id }) => id)
    );

    for (const sepWallEntity of this._selectedSepWallEntities) {
      if (!sepWallIds.has(sepWallEntity.id)) sepWallEntity.selected = false;
    }

    this._selectedSepWallEntities = [];
    for (const sepWallId of sepWallIds) {
      const sepWallEntity = modelManager.separateWallManager.getEntity(sepWallId);
      if (!sepWallEntity) continue;
      this._selectedSepWallEntities.push(sepWallEntity);
      sepWallEntity.selected = true;
    }
  }

  /**
   * 与 getPureBoxesByData 的多选分支保持一致：
   * 隔墙不出子盒（轴对齐盒对斜墙/弧墙明显大于墙体），只有一面隔墙时连容器盒也不画。
   */
  private getBoxData(multiModelData: IMultiModelData) {
    const { modelInfos } = multiModelData;
    if (!modelInfos.length) return [];

    const boxModelInfos = modelInfos.filter((modelInfo) => modelInfo.type !== EEntityType.SeparateWall);
    const sepWallCount = modelInfos.length - boxModelInfos.length;
    // 只有一面隔墙时，容器盒就是这面墙的轴对齐盒，画出来正是要避免的东西，只留墙体高亮
    if (!boxModelInfos.length && sepWallCount === 1) return [];

    const result: IBoxOutlineParams[] = [];
    // getMultiModelBoxes 已把组合的子盒摊平，不用再递归 children
    const boxes = getMultiModelBoxes({ modelInfos: boxModelInfos, skipBoundingBoxCalculate: true });

    boxes.children.forEach((box) => {
      result.push({
        ...box,
        color: selectedOutlineColor,
        linewidth: 1
      });
    });

    // 容器盒取多选数据本身的盒，范围含隔墙
    if (modelInfos.length > 1) {
      result.push({
        x: multiModelData.center.x,
        y: multiModelData.center.y,
        z: multiModelData.offGround + multiModelData.height / 2,
        width: multiModelData.width,
        thickness: multiModelData.thickness,
        height: multiModelData.height,
        rotation: multiModelData.rotation,
        color: selectedOutlineColor,
        linewidth: 1
      });
    }

    return result;
  }

  private _isValidMultiSelectData(selected?: ISelected) {
    if (!selected) {
      return false;
    }

    switch (selected.type) {
      case EEntityType.Furniture:
      case EEntityType.Assembly: {
        return true;
      }
      case EEntityType.SeparateWall: {
        return !this._onlyFurniture;
      }
      case EEntityType.MultiModel: {
        const multiModelData = globalStores.getDataByIdType('', EEntityType.MultiModel);
        if (!multiModelData?.modelInfos.length) return false;
        // 混进了不可多选的构件（如门窗）时整体不接管，避免半接管后丢构件
        return multiModelData.modelInfos.every((modelInfo) => this._isValidEntity(modelInfo));
      }
      default: {
        return false;
      }
    }
  }

  private _isValidEntity(entity: Pick<ISelected, 'type'>) {
    const _validEntityType = [EEntityType.Furniture, EEntityType.Assembly];
    if (!this._onlyFurniture) _validEntityType.push(EEntityType.SeparateWall);
    return _validEntityType.includes(entity.type);
  }

  clear() {
    this._multiModelData = null;
    this.updateHoverBoxHelpers([]);
    this.updateSelectedHelpers();
    this._selectionRectHelper.clear();
    this._startPoint = null;
    this._startClientPoint = null;
    this._drawing = false;
    this.checkedEntity = null;
    this.updateBoxSelectHint();
  }

  override dispose() {
    this.clear();
    this._disposeSet.forEach((cb) => cb());
    this._disposeSet.clear();
    this._selectionRectHelper.dispose();
    // 退出多选，面板改回按 selected 取数
    editorEvent.emit(EditorEventName.MULTI_SELECT_DATA_CHANGE, null);

    super.dispose();
  }
}

/** 获取矩形选中的实体 */
function getRectSelecteds(
  gs: TGlobalStores,
  startPoint: IPoint2d,
  endPoint: IPoint2d,
  options: { onlyFurniture?: boolean } = {}
): ISelected[] {
  const rect = [
    { x: startPoint.x, y: startPoint.y },
    { x: endPoint.x, y: startPoint.y },
    { x: endPoint.x, y: endPoint.y },
    { x: startPoint.x, y: endPoint.y }
  ];

  const mode = startPoint.x < endPoint.x ? 'contain' : 'intersect';

  // 隐藏的构件在场景里已经没有 entity（manager 是把它从列表里删掉，不是置 invisible），
  // 点选走 raycast 天然选不中；框选按 store 数据查，必须显式排掉，
  // 否则会选中看不见的构件、并给它画出包围盒
  const hiddenFurnitureIds = gs.visibilityStore.getHiddenEntitiesIdsByEntityType(EEntityType.Furniture);
  const hiddenAssemblyIds = gs.visibilityStore.getHiddenEntitiesIdsByEntityType(EEntityType.Assembly);

  const furnitureEntities = gs.modelStore
    .getFurnitureInPolygon(rect, [], mode)
    .filter(
      (furnitureData) =>
        !gs.assemblyStore.isAssemblyFuniture(furnitureData.id) && !hiddenFurnitureIds.has(furnitureData.id)
    )
    .map((furnitureData) => ({
      entityId: furnitureData.id,
      type: EEntityType.Furniture
    }));

  const assemblyEntities = gs.assemblyStore
    .getAssemblyInPolygon(rect, [], mode)
    .filter((assemblyData) => !hiddenAssemblyIds.has(assemblyData.id))
    .map((assemblyData) => ({
      entityId: assemblyData.id,
      type: EEntityType.Assembly
    }));

  if (options.onlyFurniture) return [...furnitureEntities, ...assemblyEntities];

  const hiddenSepWallIds = gs.visibilityStore.getHiddenEntitiesIdsByEntityType(EEntityType.SeparateWall);
  const separateWallEntities = gs.separateWallStore
    .getSepDatasInPolygon(rect, mode)
    .filter((sepWallData) => !hiddenSepWallIds.has(sepWallData.id))
    .map((sepWallData) => ({
      entityId: sepWallData.id,
      type: EEntityType.SeparateWall
    }));

  return [...furnitureEntities, ...assemblyEntities, ...separateWallEntities];
}

function getFurnitureBoxes(id: string): IBoxOutlineParams[] {
  const furnitureData = globalStores.modelStore.getFurnitureData(id);
  if (!furnitureData) return [];
  return [
    {
      x: furnitureData.center.x,
      y: furnitureData.center.y,
      z: furnitureData.offGround + furnitureData.height / 2,
      width: furnitureData.width,
      thickness: furnitureData.thickness,
      height: furnitureData.height,
      color: hoverOutlineColor,
      rotation: furnitureData.rotation || 0,
      linewidth: 1,
      renderOrder: ENTITY_RENDER_ORDER.hoverOutline
    }
  ];
}

/** 隔墙的 hover 高亮：叠一层不参与碰撞检测的墙体形状，与单选 hover 表现一致 */
function createSepWallHoverHelper(id: string) {
  const sepWallData = globalStores.separateWallStore.getSepData(id, false);
  if (!sepWallData) return;

  // 不透传 id：这层只是覆盖物，带上 entityId 会让它在场景里冒充真墙体
  const helper = new SeparateSelectedEntity({});
  helper.update({ ...sepWallData, status: EEntityStatus2.Hover, forceUpdate: true });
  return helper;
}

function getAssemblyBoxes(id: string): IBoxOutlineParams[] {
  const assemblyData = globalStores.assemblyStore.getAssemblyData(id);
  if (!assemblyData) return [];

  const boxes: IBoxOutlineParams[] = [];
  boxes.push({
    x: assemblyData.center.x,
    y: assemblyData.center.y,
    z: assemblyData.offGround + assemblyData.height / 2,
    width: assemblyData.width,
    thickness: assemblyData.thickness,
    height: assemblyData.height,
    color: hoverOutlineColor,
    rotation: assemblyData.rotation || 0,
    linewidth: 1,
    renderOrder: ENTITY_RENDER_ORDER.hoverOutline
  });
  assemblyData.accessorieIds.forEach((accessorieId) => {
    boxes.push(...getFurnitureBoxes(accessorieId));
  });

  return boxes;
}
